import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Download, ExternalLink } from 'lucide-react';
import {
  CHARGE_STATUSES,
  CHARGE_STATUS_LABELS,
  CHARGE_BILLING_TYPE_LABELS,
  LIVE_CHARGE_STATUSES,
  formatCents,
  type Charge,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { CHARGE_TONE } from './cobrancas-shared';

/**
 * Tudo que foi cobrado pelo gateway.
 *
 * A pergunta desta tela é outra: no contas a receber o operador olha UMA
 * conta; aqui ele olha o movimento — o que foi mandado, o que já entrou e o
 * que venceu sem ninguém pagar.
 */
export function CobrancasPage() {
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [copiado, setCopiado] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['charges', { search, status, from, to, page }],
    queryFn: () =>
      api.get<Paginated<Charge>>('/charges', {
        search: search || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        perPage: 50,
      }),
  });

  const rows = listQuery.data?.data ?? [];

  const columns: Array<Column<Charge>> = [
    { key: 'entryCode', label: 'Lançamento', mono: true, strong: true, render: (row) => row.entryCode },
    {
      key: 'personName',
      label: 'Cliente',
      render: (row) => row.personName ?? '—',
    },
    {
      key: 'entryDescription',
      label: 'Descrição',
      render: (row) => (
        <span title={row.entryDescription}>
          {row.entryDescription.length > 40
            ? `${row.entryDescription.slice(0, 40)}…`
            : row.entryDescription}
        </span>
      ),
    },
    {
      key: 'billingType',
      label: 'Forma',
      render: (row) => CHARGE_BILLING_TYPE_LABELS[row.billingType],
    },
    { key: 'dueDate', label: 'Vencimento', mono: true, render: (row) => formatDate(row.dueDate) },
    {
      key: 'valueCents',
      label: 'Valor',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.valueCents),
    },
    {
      key: 'feeCents',
      label: 'Taxa',
      right: true,
      mono: true,
      // A taxa é despesa nossa, não desconto do cliente: mostrar aqui é o que
      // deixa o custo do gateway visível em vez de diluído.
      render: (row) => (row.feeCents > 0 ? formatCents(row.feeCents) : '—'),
    },
    {
      key: 'status',
      label: '',
      width: 170,
      render: (row) => (
        <StatusBadge kind={CHARGE_TONE[row.status]}>{CHARGE_STATUS_LABELS[row.status]}</StatusBadge>
      ),
    },
    {
      key: 'link',
      label: '',
      width: 120,
      render: (row) =>
        row.invoiceUrl && LIVE_CHARGE_STATUSES.includes(row.status) ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              small
              variant="ghost"
              icon={ExternalLink}
              onClick={() => window.open(row.invoiceUrl!, '_blank', 'noopener,noreferrer')}
            >
              Abrir
            </Button>
            <Button
              small
              variant="ghost"
              icon={copiado === row.id ? Check : Copy}
              onClick={() => {
                void navigator.clipboard?.writeText(row.invoiceUrl!);
                setCopiado(row.id);
                window.setTimeout(() => setCopiado(null), 2000);
              }}
            >
              {copiado === row.id ? 'Copiado' : 'Link'}
            </Button>
          </div>
        ) : null,
    },
  ];

  const hasFilters = Boolean(search || status || from || to);
  const emAberto = rows
    .filter((row) => LIVE_CHARGE_STATUSES.includes(row.status))
    .reduce((soma, row) => soma + row.valueCents, 0);

  return (
    <>
      <WorkspaceHeader
        title="Cobranças"
        meta={[
          { label: 'Aguardando pagamento nesta página', value: formatCents(emAberto) },
        ]}
        actions={
          // A lista traz nome de cliente e valor: quem pode VER não
          // necessariamente pode levar embora num arquivo.
          can('cobrancas:exportar') ? (
          <Button
            icon={Download}
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv('cobrancas', [
                ['Lançamento', 'Cliente', 'Descrição', 'Forma', 'Vencimento', 'Valor', 'Taxa', 'Situação'],
                ...rows.map((row) => [
                  row.entryCode,
                  row.personName ?? '',
                  row.entryDescription,
                  CHARGE_BILLING_TYPE_LABELS[row.billingType],
                  formatDate(row.dueDate),
                  formatCents(row.valueCents),
                  row.feeCents > 0 ? formatCents(row.feeCents) : '',
                  CHARGE_STATUS_LABELS[row.status],
                ]),
              ])
            }
          >
            Exportar CSV
          </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por cliente, lançamento ou descrição…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          from={from}
          to={to}
          onFromChange={(value) => {
            setFrom(value);
            setPage(1);
          }}
          onToChange={(value) => {
            setTo(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: CHARGE_STATUSES.map((value) => ({
                value,
                label: CHARGE_STATUS_LABELS[value],
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
          ]}
        />

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Cobranças emitidas"
            columns={columns}
            rows={rows}
            meta={listQuery.data.meta}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setStatus('');
                    setFrom('');
                    setTo('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhuma cobrança emitida"
                  description="A cobrança nasce na parcela: abra uma conta a receber e use “Gerar cobrança”."
                />
              )
            }
          />
        )}
      </div>
    </>
  );
}
